import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "instagram",
  app: "Instagram",
  title: "How to block Instagram on iPhone — and make it hold",
  h1: "How to block Instagram",
  description:
    "The Screen Time steps, why Instagram is really three apps in one, and what to do when you close it and open it again ninety seconds later.",
  targets: [
    "how to block instagram on iphone",
    "block instagram app",
    "stop checking instagram",
    "instagram app limit iphone",
  ],
  published: "2026-09-03",
  standfirst:
    "You did not open Instagram for an hour. You opened it eleven times for five minutes, which is worse, and which no limit is designed to catch.",
  takeaways: [
    "Instagram is three products sharing an icon — a feed, a Reels tab that is a different app entirely, and a messaging service you may genuinely need. Decide which one you are blocking.",
    "The problem is usually frequency rather than duration, and a daily-total limit does not touch frequency at all.",
    "Move it off the home screen before anything else. Most Instagram opens are a thumb finding a position, not a decision.",
  ],
  mechanic: [
    "Instagram stopped being a feed some time ago. What it is now is three separate products behind one icon: the posts from people you know, a Reels tab that is a full-screen algorithmic video app in its own right, and direct messages that are, for a lot of people, a genuine communication channel they cannot simply switch off.",
    "That structure is what makes Instagram so hard to limit sensibly. Blocking the app blocks the messages, so people do not block it. Not blocking it leaves the Reels tab, which is a video feed with the same shape as any other and which the app will happily drop you into from a story, a share or a notification.",
    "And the second mechanic is frequency. Instagram is built around the check — the ring around a story, the red dot, the number on the message tab — and each of those is designed to be resolved rather than watched. A person who opens it eleven times for five minutes has lost the same hour as one long session and has also lost the concentration around each of the eleven, which is the more expensive loss.",
  ],
  limitFails:
    "Screen Time counts minutes, and Instagram's damage is mostly counted in openings. A thirty-minute daily limit is entirely compatible with checking fourteen times before lunch, and it will not fire until mid-afternoon, by which point the day's attention has already been chopped into pieces. The second failure is the messages: because a full block takes those with it, most people give themselves an exception, and the exception is the door everything else walks through.",
  instead: [
    "Take it off the home screen and out of the dock. Most opens are muscle memory finding a position, and moving the icon breaks that for weeks.",
    "Turn off every Instagram notification except direct messages. The story ring and the like count are engineered check triggers, not information.",
    "If you need the messages, use the browser version for them and delete the app. It is deliberately worse, which here is the entire point.",
    "Set the Screen Time limit on Social Networking as a whole so the frequency has nowhere to relocate to.",
  ],
  verses: [
    {
      verse: {
        text: "Keep your heart with all diligence, for out of it is the wellspring of life.",
        ref: "Proverbs 4:23",
      },
      note: "Keep is a guarding word — the same one used for watching a city or a vineyard. The instruction is not about what you feel but about what you let in, and it assumes an active perimeter rather than good intentions. A feed is a very efficient way of leaving that perimeter open all day.",
    },
    {
      verse: {
        text: "But they themselves, measuring themselves by themselves, and comparing themselves with themselves, are without understanding.",
        ref: "2 Corinthians 10:12",
      },
      note: "Paul's verdict on people keeping score against each other is intellectual rather than moral: they are without understanding, using a broken instrument. Instagram is a comparison engine by design rather than by accident, and the verse's diagnosis — that this is a category error, not merely a temptation — is the more useful frame.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Count opens, not minutes",
      id: "count-opens-not-minutes",
    },
    {
      type: "p",
      text: "Screen Time's own weekly report shows pickups as well as time, and for Instagram the pickup number is the one that tells the truth. Sixty pickups a day is not a screen-time problem, it is an inability to be alone with a thought for four minutes.",
    },
    {
      type: "p",
      text: "A daily total will not touch that. What touches it is distance: the icon somewhere your thumb does not go, the notifications off, the app not on the phone at all.",
    },
    {
      type: "h2",
      text: "The messages problem",
      id: "the-messages-problem",
    },
    {
      type: "p",
      text: "For a lot of people Instagram DMs are where a real part of their social life happens, and any advice that ignores that gets ignored in turn. The honest options are to move those conversations somewhere else, or to keep the messages in a browser tab where there is no feed attached.",
    },
    {
      type: "note",
      text: "What does not work is keeping the app for the messages and promising not to look at the rest. The app is designed to make that promise expensive to keep.",
    },
    {
      type: "h2",
      text: "The comparison, not the time",
      id: "the-comparison-not-the-time",
    },
    {
      type: "p",
      text: "Instagram's real cost is rarely the minutes. It is that a person finishes an ordinary Tuesday having been shown forty edited highlights and concludes something about their own life. That is a slower and more serious injury than a lost hour, and it is the reason a lot of people want out — {even when they cannot say why}.",
      links: {
        "even when they cannot say why": "/prayers/prayer-for-comparison",
      },
    },
  ],
  faq: [
    {
      q: "How do I block Instagram on my iPhone?",
      a: "Settings → Screen Time → App Limits → Add Limit, choose Social Networking rather than Instagram alone, turn on Block at End of Limit, and set a Screen Time passcode that is not your unlock code. Then move the icon off your home screen, which does more than the limit.",
    },
    {
      q: "Can I block the Instagram feed but keep messages?",
      a: "Not within the app. The workable version is deleting the app and using the browser for messages — there is a feed there too, but it is worse, which is what you want.",
    },
    {
      q: "Why do I keep opening Instagram without meaning to?",
      a: "Because the open is muscle memory rather than a decision — a thumb finding a position on a grid. Moving the icon breaks it for several weeks, which is why that is the first thing to do rather than the last.",
    },
  ],
  relatedApps: ["tiktok", "facebook", "pinterest"],
  related: ["screen-time-limits-dont-work", "social-media-fast-guide"],
  relatedPrayers: [
    "prayer-for-comparison",
    "prayer-before-you-open-your-phone",
  ],
};
